// Collaborative sub-teams (requirements R8). Mirrors chaos-player-poc server/utils/sub-teams.js — the
// server re-validates with the same rules. A sub-team groups the exercise's seats (scenario roles); each
// player's Viewing filter starts on their own sub-team, and they can switch the others on.
import { soloRolesFromRoleFieldValue } from "./gameSessionUtils"
import { resolveSoloRolesSync } from "./roleMatchUtils"

export const SUB_TEAMS_MAX = 6
export const SUB_TEAM_NAME_MAX = 60
// Wizard form fields (Step 2, Collaborative). New names on purpose: the old `_showOtherPlayersActions`
// toggle was retired 2026-09-05 and stale drafts may still carry it.
export const SUB_TEAM_FORM_FIELDS = Object.freeze({
  PLAYERS_SEE_EACH_OTHER: "_playersSeeEachOther", // boolean, default true
  SUB_TEAMS: "_subTeams", // [{ id, name, roles }]
})

const clean = (v) =>
  String(v ?? "")
    .replace(/\s+/g, " ")
    .trim()
const norm = (v) => clean(v).toLowerCase()

let idSeq = 0
export const newSubTeamId = () => `team-${Date.now().toString(36)}-${(idSeq += 1)}`
export const emptySubTeam = () => ({ id: newSubTeamId(), name: "", roles: [] })

// First problem with a draft, or "" when it is fine to save. Same rules as the server.
export const subTeamsDraftError = (teams) => {
  if (!Array.isArray(teams) || teams.length === 0) return ""
  if (teams.length < 2) return "Set up at least two sub-teams, or remove them."
  if (teams.length > SUB_TEAMS_MAX) return `An exercise can have at most ${SUB_TEAMS_MAX} sub-teams.`
  const names = new Set()
  for (const [i, t] of teams.entries()) {
    const name = clean(t?.name)
    if (!name) return `Sub-team ${i + 1} needs a name.`
    if (name.length > SUB_TEAM_NAME_MAX) return `Sub-team names can be at most ${SUB_TEAM_NAME_MAX} characters.`
    if (names.has(norm(name))) return `Two sub-teams are named "${name}".`
    names.add(norm(name))
    if (!(t?.roles ?? []).length) return `Sub-team "${name}" needs at least one role.`
  }
  return ""
}

// What is sent to the server: trimmed, only roles that still exist on the exercise.
export const subTeamsForSave = ({ teams, scenarioRoles = [] }) => {
  if (!Array.isArray(teams)) return []
  const known = new Set(scenarioRoles.map(norm))
  return teams.map((t) => ({
    id: t.id,
    name: clean(t.name),
    roles: (t.roles ?? []).filter((r) => known.size === 0 || known.has(norm(r))),
  }))
}

// Toggle a role into a team. A role lives in at most one team, so picking it in another team MOVES it.
export const assignRoleToTeam = ({ teams, teamId, role }) => {
  const inTarget = teams.find((t) => t.id === teamId)?.roles?.some((r) => norm(r) === norm(role))
  return teams.map((t) => {
    const without = (t.roles ?? []).filter((r) => norm(r) !== norm(role))
    if (t.id !== teamId) return { ...t, roles: without }
    return { ...t, roles: inTarget ? without : [...without, role] }
  })
}

export const teamForRole = (teams, role) => (teams ?? []).find((t) => (t.roles ?? []).some((r) => norm(r) === norm(role))) ?? null

// Roles no sub-team claims — "Shared": they see everyone and everyone sees them.
export const sharedRoles = ({ teams, scenarioRoles = [] }) => scenarioRoles.filter((r) => !teamForRole(teams, r))

// The seats a roster row's free-text Role resolves to (same deterministic tiers the launch mapper uses).
export const seatsForRosterRole = ({ roleValue, scenarioRoles = [] }) => {
  const typed = soloRolesFromRoleFieldValue(roleValue)
  return typed.length ? resolveSoloRolesSync({ requestedRoles: typed, scenarioRoles, strict: true, initials: true }).resolved : []
}

// People whose seats fall in more than one sub-team (allowed — they belong to both — but made visible).
export const peopleInSeveralTeams = ({ teams, roster = [], scenarioRoles = [] }) =>
  roster
    .map((row) => {
      const seats = seatsForRosterRole({ roleValue: row?.role, scenarioRoles })
      const teamIds = [...new Set(seats.map((s) => teamForRole(teams, s)?.id).filter(Boolean))]
      return { name: clean(row?.name) || clean(row?.email), teamIds }
    })
    .filter((p) => p.name && p.teamIds.length > 1)

// ── Gameplay ────────────────────────────────────────────────────────────────
// The sub-teams a viewer belongs to, from the roles they hold in the exercise.
export const subTeamsForRoles = (teams, roles = []) => {
  const ids = new Set()
  for (const r of roles ?? []) {
    const t = teamForRole(teams, r)
    if (t) ids.add(t.id)
  }
  return (teams ?? []).filter((t) => ids.has(t.id))
}

// "Incident Response (IR Lead, SOC Lead, IT Lead) · Legal & Finance (Legal, Finance) · Shared: CISO"
export const subTeamsSummary = ({ teams, scenarioRoles = [] }) => {
  if (!Array.isArray(teams) || !teams.length) return ""
  const parts = teams.map((t) => `${clean(t.name)} (${(t.roles ?? []).join(", ")})`)
  const shared = sharedRoles({ teams, scenarioRoles })
  if (shared.length) parts.push(`Shared: ${shared.join(", ")}`)
  return parts.join(" · ")
}
